import {isNonsensitiveSchema} from 'src/schema/nonsensitive';
import {fallback, getGlobalConfig, undefined as vUndefined} from 'valibot';

import type {StandardSchemaV1} from '@standard-schema/spec';
import type {
  ArraySchema,
  BaseIssue,
  BaseSchema,
  ObjectEntries,
  ObjectSchema,
  OptionalSchema,
  RecordSchema,
  SchemaWithPipe,
  TupleSchema,
  UnionOptions,
  UnionSchema,
  VariantOptions,
  VariantSchema,
} from 'valibot';

type GenericSchema<T = unknown> = BaseSchema<T, T, BaseIssue<unknown>>;
type SchemaOrPipe<T = unknown> =
  | BaseSchema<T, T, BaseIssue<unknown>>
  | ArraySchema<GenericSchema, undefined>
  | ObjectSchema<{[k: string]: GenericSchema}, undefined>
  | OptionalSchema<GenericSchema, undefined>
  | RecordSchema<GenericSchema<string>, GenericSchema, undefined>
  | UnionSchema<UnionOptions, undefined>
  | VariantSchema<string, VariantOptions<string>, undefined>
  | SchemaWithPipe<[GenericSchema]>
  | TupleSchema<GenericSchema[], undefined>;

export function getNonSensitiveSchemas<TSchema extends SchemaOrPipe>(
  schema: TSchema,
  skippedKeys: string[] = [],
): TSchema | undefined {
  const res = {...schema}; // Clone the schema
  // TODO(imolorhe): Currently valibot creates the ~standard prop using a _getStandardProps function which
  //  creates a closure over the schema, so mutating the schema doesn't affect the standard schema validation.
  //  This is a workaround to ensure that the standard schema is created with the correct schema.
  // @ts-expect-error Cannot assign to ''~standard'' because it is a read-only property
  res['~standard'] = _getStandardProps(res);
  return _getNonSensitiveSchemas(res, skippedKeys);
}

function _getNonSensitiveSchemas<TSchema extends SchemaOrPipe>(
  schema: TSchema,
  skippedKeys: string[] = [],
): TSchema | undefined {
  // If it is an object schema, return nonsensitive schema of entries
  if (isObjectSchema(schema)) {
    // @ts-expect-error Cannot assign to 'entries' because it is a read-only property.
    schema.entries = _getObjectEntriesNonsensitiveSchema(
      {...schema.entries},
      skippedKeys,
    );
    return schema;
  }

  if (isRecordSchema(schema)) {
    const nonSensitiveKeySchema = _getNonSensitiveSchemas(schema.key);
    if (nonSensitiveKeySchema) {
      const nonSensitiveValueSchema = _getNonSensitiveSchemas(schema.value);
      if (nonSensitiveValueSchema) {
        // @ts-expect-error Cannot assign to 'value' because it is a read-only property.
        schema.value = nonSensitiveValueSchema;
        // schema.key does not need to be overwritten here as it's verified safe at this point
        // it's a primitive type and marked as non-sensitive (checked above)
        return schema;
      }
    }
    // We're zeroing out the schema making sure it won't match anything.
    // Fallback is needed to not run into invalid schema error that returns undefined
    // @ts-expect-error Cannot assign to 'key' because it is a read-only property.
    schema.value = fallback(vUndefined(), undefined);
    return schema;
  }

  // If it is an optional schema, traverse the wrapped schema and re-wrap in optional
  if (isOptionalSchema(schema)) {
    const nonSensitiveWrapped = getNonSensitiveSchemas(schema.wrapped);
    if (!nonSensitiveWrapped) {
      return;
    }
    // @ts-expect-error Cannot assign to 'wrapped' because it is a read-only property.
    schema.wrapped = nonSensitiveWrapped;
    return schema;
  }

  // If it is an array schema, return update the item schema
  if (isArraySchema(schema)) {
    const itemSchema = getNonSensitiveSchemas(schema.item);
    if (!itemSchema) {
      return;
    }
    // @ts-expect-error Cannot assign to 'item' because it is a read-only property.
    schema.item = itemSchema;
    return schema;
  }

  // If it is a tuple schema, return defaults of items
  if (isTupleSchema(schema)) {
    // @ts-expect-error Cannot assign to 'items' because it is a read-only property.
    schema.items = schema.items.map(getNonSensitiveSchemas).filter(Boolean);
  }

  if (isVariantSchema(schema)) {
    // @ts-expect-errorCannot assign to 'options' because it is a read-only property.
    schema.options = _getVariantOptionsNonsensitiveSchema(
      schema.key,
      schema.options,
    ).filter(Boolean);

    return schema;
  }

  if (isUnionSchema(schema)) {
    // @ts-expect-error Cannot assign to 'options' because it is a read-only property.
    schema.options = _getUnionOptionsNonsensitiveSchema(schema.options).filter(
      Boolean,
    );

    return schema;
  }

  // Otherwise, return nonsensitive schema or `undefined`
  return _getNonSensitiveSchema(schema);
}

function _getNonSensitiveSchema<TSchema extends SchemaOrPipe>(
  schema: TSchema,
): TSchema | undefined {
  // Perform filteration based on the presence of the nonsensitive annotation
  if ('pipe' in schema) {
    const hasNonsensitiveMarker = schema.pipe.some((item) =>
      isNonsensitiveSchema(item),
    );
    if (hasNonsensitiveMarker) {
      return schema;
    }
  }
  if (isNonsensitiveSchema(schema)) {
    return schema;
  }

  // TODO(imolorhe): Do we want to validate and fail if the data contains sensitive data or just filter it out?
  // return custom(() => false);
}

function _getObjectEntriesNonsensitiveSchema(
  entries: {
    [k: string]: GenericSchema<unknown>;
  },
  skippedKeys: string[] = [],
): {[k: string]: GenericSchema<unknown>} {
  Object.entries(entries).forEach(([key, value]) => {
    // if key is in skippedKeys, skip it
    // This is useful for skipping keys that are not supposed to be sensitive
    // e.g. the discriminator key in a variant schema
    if (skippedKeys.includes(key)) {
      return;
    }
    const nonSensitiveSchema = getNonSensitiveSchemas(value);
    if (!nonSensitiveSchema) {
      Reflect.deleteProperty(entries, key);
    } else if (
      isObjectSchema(nonSensitiveSchema) &&
      Object.keys(nonSensitiveSchema.entries).length === 0
    ) {
      // If all entries in an object schema are removed, remove the key
      Reflect.deleteProperty(entries, key);
    } else if (
      isArraySchema(nonSensitiveSchema) &&
      isObjectSchema(nonSensitiveSchema.item) &&
      Object.keys(nonSensitiveSchema.item.entries).length === 0
    ) {
      // If all entries in an array schema item object schema are removed, remove the key
      Reflect.deleteProperty(entries, key);
    } else if (
      isOptionalSchema(nonSensitiveSchema) &&
      isObjectSchema(nonSensitiveSchema.wrapped) &&
      Object.keys(nonSensitiveSchema.wrapped.entries).length === 0
    ) {
      // If all entries in an optional-wrapped object schema are removed, remove the key
      Reflect.deleteProperty(entries, key);
    } else if (
      isUnionSchema(nonSensitiveSchema) &&
      nonSensitiveSchema.options.length === 0
    ) {
      // If all options in a union schema are removed, remove the key
      Reflect.deleteProperty(entries, key);
    } else {
      entries[key] = nonSensitiveSchema;
    }
  });

  return entries;
}

function _getVariantOptionsNonsensitiveSchema(
  key: string,
  options: VariantOptions<string>,
): (SchemaOrPipe | undefined)[] {
  return options.map((value) => {
    const nonSensitiveSchema = getNonSensitiveSchemas(value, [key]);
    if (!nonSensitiveSchema) {
      // remove the schema if it is undefined
      return;
    }
    if (
      isObjectSchema(nonSensitiveSchema) &&
      Object.keys(nonSensitiveSchema.entries).length === 0
    ) {
      // If all entries in an object schema are removed, remove the schema
      return;
    }

    // Otherwise, return the non-sensitive schema
    return nonSensitiveSchema;
  });
}

function _getUnionOptionsNonsensitiveSchema(
  options: UnionOptions,
): (SchemaOrPipe | undefined)[] {
  return options.map((value) => {
    const nonSensitiveSchema = getNonSensitiveSchemas(value);
    if (!nonSensitiveSchema) {
      return;
    }
    if (
      isObjectSchema(nonSensitiveSchema) &&
      Object.keys(nonSensitiveSchema.entries).length === 0
    ) {
      return;
    }

    return nonSensitiveSchema;
  });
}

function _getStandardProps<
  TSchema extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
>(
  context: TSchema,
): StandardSchemaV1.Props<
  StandardSchemaV1.InferInput<TSchema>,
  StandardSchemaV1.InferOutput<TSchema>
> {
  return {
    version: 1,
    vendor: 'valibot',
    validate(
      value,
    ): StandardSchemaV1.Result<StandardSchemaV1.InferOutput<TSchema>> {
      const run = context?.['~run'].bind(context);
      if (!run) {
        throw new Error(
          'The schema does not have a run function. Ensure that the schema is created with `getNonSensitiveSchemas`.',
        );
      }
      return run({value}, getGlobalConfig()) as StandardSchemaV1.Result<
        StandardSchemaV1.InferOutput<TSchema>
      >;
    },
  };
}

function isObjectSchema(
  schema: SchemaOrPipe,
): schema is ObjectSchema<ObjectEntries, undefined> {
  return 'entries' in schema && schema.type === 'object';
}

function isRecordSchema(
  schema: SchemaOrPipe,
): schema is RecordSchema<GenericSchema<string>, GenericSchema, undefined> {
  return 'key' in schema && 'value' in schema && schema.type === 'record';
}

function isArraySchema(
  schema: SchemaOrPipe,
): schema is ArraySchema<GenericSchema, undefined> {
  return 'item' in schema && schema.type === 'array';
}

function isVariantSchema(
  schema: SchemaOrPipe,
): schema is VariantSchema<string, VariantOptions<string>, undefined> {
  return 'options' in schema && schema.type === 'variant';
}

function isTupleSchema(
  schema: SchemaOrPipe,
): schema is TupleSchema<GenericSchema[], undefined> {
  return 'items' in schema && schema.type === 'tuple';
}

function isUnionSchema(
  schema: SchemaOrPipe,
): schema is UnionSchema<UnionOptions, undefined> {
  return 'options' in schema && schema.type === 'union';
}

function isOptionalSchema(
  schema: SchemaOrPipe,
): schema is OptionalSchema<GenericSchema, undefined> {
  // Exclude schemas that also have a 'pipe' property — those are pipe-wrapped
  // schemas (e.g. pipe(optional(...), nonsensitive())) and should be handled
  // by _getNonSensitiveSchema which checks for the nonsensitive annotation.
  return (
    'wrapped' in schema && schema.type === 'optional' && !('pipe' in schema)
  );
}
